import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {
  FeeSnapshotItem,
  FeeType,
  FlowDirection,
  ProvidersBaseFees,
} from '@gatewee/types/fee';
import {PaymentMethod} from '@gatewee/types/payment';
import {PaymentProvider} from '@gatewee/types/provider';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../../gatewee-input-v2/gatewee-input-v2.component';
import {GateweeSelectV3Component} from '../../gatewee-select-v3/gatewee-select-v3.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'providers-base-fees-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeInputV2Component,
    GateweeSelectV3Component,
  ],
  templateUrl: './providers-base-fees-parameter.component.html',
})
export class ProvidersBaseFeesParameterComponent implements OnInit {
  feeForm!: FormGroup;
  feeRows!: FormArray;
  providerToAdd = new FormControl<PaymentProvider | null>(null);
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  draft: Partial<ProvidersBaseFees> = {};
  activeProvider: PaymentProvider | null = null;
  addProviderItems: {value: PaymentProvider; label: string}[] = [];
  readonly allProviders = Object.values(PaymentProvider);
  readonly PaymentMethod = PaymentMethod;
  readonly FlowDirection = FlowDirection;
  readonly feeTypeItems = [
    {
      value: 'amount' as FeeType,
      label: 'features.settings.parameters.providersBaseFees.type.amount',
    },
    {
      value: 'rate' as FeeType,
      label: 'features.settings.parameters.providersBaseFees.type.rate',
    },
  ];

  readonly paymentMethodItems = [
    {
      value: '',
      label:
        'features.settings.parameters.providersBaseFees.row.paymentMethodAny',
    },
    ...Object.values(PaymentMethod).map(m => ({
      value: m,
      label: `features.settings.parameters.paymentMethods.${m}.label`,
    })),
  ];

  readonly flowItems = [
    {
      value: FlowDirection.INCOME,
      label: 'features.settings.parameters.providersBaseFees.flow.income',
    },
    {
      value: FlowDirection.CASH_OUT,
      label: 'features.settings.parameters.providersBaseFees.flow.cashOut',
    },
  ];

  /** Shown when payment method is credit card; `''` = omit installments (any). */
  readonly installmentItems: Array<{value: number | ''; label: string}> = [
    {
      value: '',
      label:
        'features.settings.parameters.providersBaseFees.row.installmentsAny',
    },
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => ({
      value: n,
      label: String(n),
    })),
  ];

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
  }

  ngOnInit(): void {
    this.draft = {...this.parseValue()};
    this.feeRows = this.fb.array([this.createFeeRow()]);
    this.feeForm = this.fb.group({rows: this.feeRows});
    this.refreshAddProviderItems();
  }

  private refreshAddProviderItems(): void {
    this.addProviderItems = this.allProviders
      .filter(p => this.draft[p] === undefined)
      .map(p => ({value: p, label: p}));
  }

  get configuredProviders(): PaymentProvider[] {
    return Object.keys(this.draft).filter(
      k => this.draft[k as PaymentProvider] !== undefined,
    ) as PaymentProvider[];
  }

  private parseValue(): Partial<ProvidersBaseFees> {
    const raw = this.setting.value;
    if (!raw) return {};
    try {
      const obj =
        typeof raw === 'string'
          ? (JSON.parse(raw) as Partial<ProvidersBaseFees>)
          : (raw as Partial<ProvidersBaseFees>);
      return typeof obj === 'object' && obj !== null ? obj : {};
    } catch {
      return {};
    }
  }

  private loadRowsForProvider(p: PaymentProvider): void {
    while (this.feeRows.length) {
      this.feeRows.removeAt(0);
    }
    const rows = this.draft[p] ?? [];
    if (rows.length > 0) {
      for (const r of rows) {
        this.feeRows.push(this.createFeeRow(r));
      }
    } else {
      this.feeRows.push(this.createFeeRow());
    }
  }

  addProvider(): void {
    const p = this.providerToAdd.value;
    if (!p || this.draft[p] !== undefined) return;
    this.draft = {...this.draft, [p]: []};
    this.activeProvider = p;
    this.loadRowsForProvider(p);
    this.providerToAdd.setValue(null);
    this.refreshAddProviderItems();
  }

  editProvider(p: PaymentProvider): void {
    this.activeProvider = p;
    this.loadRowsForProvider(p);
  }

  removeProvider(p: PaymentProvider): void {
    const rest = {...this.draft};
    delete rest[p];
    this.draft = rest as Partial<ProvidersBaseFees>;
    if (this.activeProvider === p) {
      this.activeProvider = null;
      while (this.feeRows.length) {
        this.feeRows.removeAt(0);
      }
      this.feeRows.push(this.createFeeRow());
    }
    this.refreshAddProviderItems();
  }

  addRow(): void {
    this.feeRows.push(this.createFeeRow());
  }

  removeRow(index: number): void {
    if (this.feeRows.length > 1) {
      this.feeRows.removeAt(index);
    }
  }

  private createFeeRow(r?: FeeSnapshotItem): FormGroup {
    return this.fb.group({
      type: [(r?.type ?? 'rate') as FeeType, Validators.required],
      value: [r?.value ?? 0, [Validators.required]],
      paymentMethod: [r?.paymentMethod ?? ''],
      operation: [r?.operation ?? FlowDirection.INCOME],
      installments: [
        r?.installments !== undefined && r?.installments !== null
          ? r.installments
          : '',
      ],
    });
  }

  private mapRowsToSnapshotItems(
    rows: Array<{
      type: FeeType;
      value: number;
      paymentMethod?: string;
      operation?: string;
      installments?: number | string | '';
    }>,
  ): FeeSnapshotItem[] {
    return rows
      .filter(row => row.type && row.value !== undefined && row.value !== null)
      .map(row => this.feeSnapshotFromRow(row));
  }

  private feeSnapshotFromRow(row: {
    type: FeeType;
    value: number;
    paymentMethod?: string;
    operation?: string;
    installments?: number | string | '';
  }): FeeSnapshotItem {
    const item: FeeSnapshotItem = {
      type: row.type,
      value: Number(row.value),
    };
    if (row.paymentMethod) {
      item.paymentMethod = row.paymentMethod as PaymentMethod;
    }
    if (row.operation) {
      item.operation = row.operation as FlowDirection;
    }
    if (row.paymentMethod === PaymentMethod.CREDIT_CARD) {
      const inst = row.installments;
      if (inst !== '' && inst !== undefined && inst !== null) {
        const n = Number(inst);
        if (!Number.isNaN(n) && n > 0) {
          item.installments = n;
        }
      }
    }
    return item;
  }

  /** Snapshot rows from form into draft for the active provider (valid rows only). */
  apply(): void {
    if (!this.activeProvider || this.feeRows.invalid) {
      this.feeRows.markAllAsTouched();
      return;
    }
    const rows = this.feeRows.value as Array<{
      type: FeeType;
      value: number;
      paymentMethod?: string;
      operation?: string;
      installments?: number | string | '';
    }>;
    const items = this.mapRowsToSnapshotItems(rows);
    this.draft = {
      ...this.draft,
      [this.activeProvider]: items,
    };
  }

  onSend(): void {
    if (this.submitting) return;
    if (this.activeProvider) {
      if (this.feeRows.invalid) {
        this.feeRows.markAllAsTouched();
        return;
      }
      this.apply();
    }
    this.submitting = true;
    const out: Partial<Record<PaymentProvider, FeeSnapshotItem[]>> = {};
    for (const p of Object.keys(this.draft) as PaymentProvider[]) {
      const rows = this.draft[p] ?? [];
      const items = this.mapRowsToSnapshotItems(
        rows as Array<{
          type: FeeType;
          value: number;
          paymentMethod?: string;
          operation?: string;
          installments?: number | string | '';
        }>,
      );
      if (items.length > 0) {
        out[p] = items;
      }
    }
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: JSON.stringify(out),
    };
    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
